// components/MachineOverview.tsx

import React, { useEffect, useState } from 'react';
import jwt_decode from 'jwt-decode';
import './MachineOverview.css';
import { useNavigate, Link } from 'react-router-dom';
import { updateRecord } from './DataHandler';
import { deleteRecord, fetchRecordsByEmail } from './DataHandler';
import { FaTrash as FaTrashIcon } from 'react-icons/fa';

const FaTrash = FaTrashIcon as any;

interface Record {
  mac: string;
  name?: string[];
  in?: number[];
  out?: number[];
  type?: number;
  temperature?: number;
  humidity?: number;
}

interface MachineOverviewProps {
  mqttMessage: string;
}

const MachineOverview: React.FC<MachineOverviewProps> = ({ mqttMessage }) => {
  const [email, setEmail] = useState('');
  const [records, setRecords] = useState<Record[]>([]);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('token');
    const email = token ? (jwt_decode(token) as { email: string }).email : '';
    setEmail(email);
  }, []);

  useEffect(() => {
    if (email) {
      fetchRecordsByEmail(email)
        .then((data) => {
          const updated = data.map((rec: any) => ({
            ...rec,
            temperature: rec.temperature ?? rec.temp,
            humidity: rec.humidity ?? rec.humi,
          }));
          setRecords(updated);
        })
        .catch((error) => console.error('Error fetching records:', error));
    }
  }, [email]);

  useEffect(() => {
    if (mqttMessage && email) {
      try {
        const parsedMessage = JSON.parse(mqttMessage);

        if (parsedMessage.email === email) {
          setRecords((prevRecords) => {
            const existingRecordIndex = prevRecords.findIndex(
              (record) => record.mac === parsedMessage.mac
            );

            if (existingRecordIndex >= 0) {
              return prevRecords.map((record, index) =>
                index === existingRecordIndex
                  ? {
                    ...record,
                    in: parsedMessage.in,
                    out: parsedMessage.out,
                    type: parsedMessage.type,
                    temperature: parsedMessage.temperature ?? parsedMessage.temp,
                    humidity: parsedMessage.humidity ?? parsedMessage.humi,
                  }
                  : record
              );
            }

            return [
              ...prevRecords,
              {
                mac: parsedMessage.mac,
                name: parsedMessage.name || parsedMessage.mac,
                in: parsedMessage.in || [],
                out: parsedMessage.out || [],
                type: parsedMessage.type,
                temperature: parsedMessage.temperature ?? parsedMessage.temp,
                humidity: parsedMessage.humidity ?? parsedMessage.humi,
              },
            ];
          });

          updateRecord(parsedMessage.mac, parsedMessage).catch((error) =>
            console.error('Error upserting data:', error)
          );
        }
      } catch (error) {
        console.error('MQTT 메시지 파싱 오류:', error);
      }
    }
  }, [mqttMessage, email]);

  const handleDelete = async (mac: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (window.confirm('정말로 삭제하시겠습니까?')) {
      try {
        await deleteRecord(mac);
        setRecords((prevRecords) => prevRecords.filter((record) => record.mac !== mac));
        alert('삭제되었습니다.');
      } catch (error) {
        console.error('Error deleting record:', error);
        alert('삭제 중 오류가 발생했습니다.');
      }
    }
  };

  return (
    <div className="machine-overview">
      <div className="table-responsive">
        <table className="machine-table">
          <thead>
            <tr>
              <th style={{ width: '20%' }}>이름</th>
              <th>모니터</th>
            </tr>
          </thead>
          <tbody>
            {records.length > 0 ? (
              records.map((record) => (
                <tr key={record.mac}>
                  <td>
                    <Link
                      to={`/device/${record.mac}`}
                      style={{ textDecoration: 'none' }}
                    >
                      <span style={{ cursor: 'pointer', color: '#007bff', fontWeight: 'bold' }}>
                        {record.name && Array.isArray(record.name) && record.name.length > 0
                          ? record.name[0]
                          : record.mac}
                      </span>
                    </Link>

                  </td>
                  <td>
                    <div className="monitor-container">
                      <div className="led-row">
                        <span style={{ fontWeight: 'bold', marginRight: '5px' }}>입력:</span>
                        {record.in?.map((state, index) => (
                          <div
                            key={`in-${index}`}
                            className={`led ${state === 1 ? 'led-on' : 'led-off'}`}
                            title={`Input ${index + 1}`}
                          ></div>
                        ))}
                        {(!record.in || record.in.length === 0) && <span style={{ color: '#aaa', fontSize: '0.9rem' }}>--</span>}
                      </div>

                      <div className="led-row output-row">
                        <span style={{ fontWeight: 'bold', marginRight: '5px', whiteSpace: 'nowrap' }}>출력:</span>
                        {record.out?.map((state, index) => (
                          <div
                            key={`out-${index}`}
                            className={`led ${state === 1 ? 'led-on' : 'led-off'}`}
                            title={`Output ${index + 1}`}
                          ></div>
                        ))}
                        {(!record.out || record.out.length === 0) && <span style={{ color: '#aaa', fontSize: '0.9rem' }}>--</span>}
                      </div>

                      <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '15px' }}>
                        {(record.temperature !== undefined || record.temperature === 0) && (
                          <span>
                            <b>온도:</b> {record.temperature}°C
                          </span>
                        )}
                        {(record.humidity !== undefined || record.humidity === 0) && (
                          <span>
                            <b>습도:</b> {record.humidity}%
                          </span>
                        )}

                        <FaTrash
                          className="trash-icon"
                          onClick={(e: React.MouseEvent) => handleDelete(record.mac, e)}
                          title="삭제"
                        />
                      </div>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={2} style={{ textAlign: 'center', padding: '40px', color: '#888' }}>
                  등록된 장치가 없습니다.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <p style={{ marginTop: '10px', color: '#666', fontSize: '0.9rem' }}>
        * 장치 이름을 클릭하면 상세 제어 화면으로 이동합니다.
      </p>
    </div >
  );
};

export default MachineOverview;
